import { create } from 'zustand';
import supplierSolutionsService, {
  CatalogItem,
  CatalogItemInput,
  ListCatalogParams,
  ListTemplatesParams,
  SolutionTemplate,
  SolutionTemplateInput,
  SolutionTemplateWithItems
} from '../services/supplierCrm/supplierSolutionsService';

interface SupplierSolutionsState {
  catalog: CatalogItem[];
  catalogCount: number | null;
  templates: SolutionTemplate[];
  templatesCount: number | null;
  loadingCatalog: boolean;
  loadingTemplates: boolean;
  saving: boolean;
  error: string | null;
  fetchCatalog: (params: ListCatalogParams) => Promise<void>;
  fetchTemplates: (params: ListTemplatesParams) => Promise<void>;
  createCatalogItem: (payload: CatalogItemInput) => Promise<CatalogItem>;
  updateCatalogItem: (id: string, payload: Partial<CatalogItemInput> & { organization_id: string }) => Promise<CatalogItem>;
  deleteCatalogItem: (id: string, organization_id: string) => Promise<void>;
  createTemplate: (payload: SolutionTemplateInput) => Promise<SolutionTemplateWithItems>;
  updateTemplate: (id: string, payload: Partial<SolutionTemplateInput> & { organization_id: string }) => Promise<SolutionTemplateWithItems>;
  deleteTemplate: (id: string, organization_id: string) => Promise<void>;
  duplicateTemplate: (id: string, organization_id: string, newName?: string) => Promise<SolutionTemplate>;
  setError: (message: string | null) => void;
  reset: () => void;
}

export const useSupplierSolutionsStore = create<SupplierSolutionsState>((set, get) => ({
  catalog: [],
  catalogCount: null,
  templates: [],
  templatesCount: null,
  loadingCatalog: false,
  loadingTemplates: false,
  saving: false,
  error: null,
  async fetchCatalog(params) {
    set({ loadingCatalog: true, error: null });
    try {
      const { data, count } = await supplierSolutionsService.listCatalog(params);
      set({ catalog: data ?? [], catalogCount: count ?? null });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load catalog items' });
      throw err;
    } finally {
      set({ loadingCatalog: false });
    }
  },
  async fetchTemplates(params) {
    set({ loadingTemplates: true, error: null });
    try {
      const { data, count } = await supplierSolutionsService.listTemplates(params);
      set({ templates: data ?? [], templatesCount: count ?? null });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load solution templates' });
      throw err;
    } finally {
      set({ loadingTemplates: false });
    }
  },
  async createCatalogItem(payload) {
    set({ saving: true, error: null });
    try {
      const { data } = await supplierSolutionsService.createCatalog(payload);
      const { catalog } = get();
      set({ catalog: [data, ...catalog] });
      return data;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to create catalog item' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async updateCatalogItem(id, payload) {
    set({ saving: true, error: null });
    try {
      const { data } = await supplierSolutionsService.updateCatalog(id, payload);
      const { catalog } = get();
      set({ catalog: catalog.map((item) => (item.id === data.id ? data : item)) });
      return data;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to update catalog item' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async deleteCatalogItem(id, organization_id) {
    set({ saving: true, error: null });
    try {
      await supplierSolutionsService.removeCatalog(id, organization_id);
      const { catalog } = get();
      set({ catalog: catalog.filter((item) => item.id !== id) });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to delete catalog item' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async createTemplate(payload) {
    set({ saving: true, error: null });
    try {
      const response = await supplierSolutionsService.createTemplate(payload);
      const { templates } = get();
      set({ templates: [response.data, ...templates] });
      return response;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to create solution template' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async updateTemplate(id, payload) {
    set({ saving: true, error: null });
    try {
      const response = await supplierSolutionsService.updateTemplate(id, payload);
      const { templates } = get();
      set({ templates: templates.map((tpl) => (tpl.id === response.data.id ? response.data : tpl)) });
      return response;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to update solution template' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async deleteTemplate(id, organization_id) {
    set({ saving: true, error: null });
    try {
      await supplierSolutionsService.removeTemplate(id, organization_id);
      const { templates } = get();
      set({ templates: templates.filter((item) => item.id !== id) });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to delete template' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async duplicateTemplate(id, organization_id, newName?) {
    set({ saving: true, error: null });
    try {
      const { data } = await supplierSolutionsService.duplicateTemplate(id, organization_id, newName);
      const { templates } = get();
      set({ templates: [data, ...templates] });
      return data;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to duplicate template' });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  setError(message) {
    set({ error: message });
  },
  reset() {
    set({
      catalog: [],
      catalogCount: null,
      templates: [],
      templatesCount: null,
      loadingCatalog: false,
      loadingTemplates: false,
      saving: false,
      error: null
    });
  }
}));
